<script setup lang="ts">
import type { GuestbookMessage } from "@/models/message"
import { computed, ref } from "vue"
import { useGetEventVault, useListGuestbook } from "@/api"
import { FlagContentModal, GuestbookMessageCard, GuestbookMessageDrawer, GuestEmptyStatePanel, MessageDeleteModal, MessageEditModal } from "@/components"
import { Button, Icon } from "@/components/ui"
import { useToast } from "@/components/ui/ToastReka/useToast"
import { useModal, useTranslation } from "@/composables"
import { EventVaultStatus } from "@/models"
import { GuestbookMessageType } from "@/models/message"
import { useEventVaultStore } from "@/stores/eventVaultStore"
import { FlagContentModalKey, GuestbookMessageDrawerKey, MessageDeleteModalKey, MessageEditModalKey } from "@/symbols"

const props = defineProps<{
  eventVaultId: number
  eventAlbumId?: number
}>()

const emit = defineEmits<{
  (e: "backToGallery"): void
}>()

const eventVaultStore = useEventVaultStore()

const eventVaultId = computed(() => props.eventVaultId)
const albumId = computed(() => props.eventAlbumId)

const { t } = useTranslation()

const toast = useToast()

const { data: eventVault, isLoading: isLoadingEventVault } = useGetEventVault(eventVaultId)
const { data: messages, isLoading: isLoadingMessages, isFetching: isFetchingMessages, isRefetching: isRefetchingMessages } = useListGuestbook(eventVaultId, albumId)

const isLoading = computed<boolean>(() => Boolean(isLoadingEventVault.value || isLoadingMessages.value || isFetchingMessages.value || isRefetchingMessages.value))
const isFeatureDisabled = computed<boolean>(() => eventVault.value?.status === EventVaultStatus.Expired)
const isGuestbookHidden = computed<boolean>(() => Boolean(eventVault.value?.eventVaultSettings?.hideGuestbook))
const canUploadVideos = computed<boolean>(() => Boolean(eventVault.value?.eventVaultSettings?.allowVideoGuestbook && eventVault.value?.eventVaultSettings.videoEnabled))

const selectedMessage = ref<GuestbookMessage | undefined>()

const { toggle: toggleDelete } = useModal(MessageDeleteModalKey)
const { toggle: toggleEdit } = useModal(MessageEditModalKey)
const { toggle: toggleFlagContentModal } = useModal(FlagContentModalKey)
const { toggle: toggleGuestbookMessageDrawer } = useModal(GuestbookMessageDrawerKey)

function openDialog(message: GuestbookMessage, toggleFn: () => void) {
  selectedMessage.value = message
  toggleFn()
}

type MessageDrawerMode = "text" | "video"
const messageDrawerMode = ref<MessageDrawerMode>("text")

function showMessageDrawer(type: MessageDrawerMode) {
  messageDrawerMode.value = type
  toggleGuestbookMessageDrawer()
}
</script>

<template>
  <div>
    <div class="flex flex-col gap-4 py-4">
      <div class="flex items-center justify-between flex-wrap gap-2">
        <div class="flex items-center gap-2">
          <Icon icon="BookOpen" />
          <h1 data-test="guestbook-messages-title" class="font-comfortaa text-2xl">
            {{ t("guestbook_messages_page.title") }}
          </h1>
        </div>
        <Button
          data-test="guestbook-back-to-gallery-button"
          variant="tertiary"
          prefix-icon="Images"
          @click="emit('backToGallery')"
        >
          <span class="hidden md:block">{{ t('back_to_gallery') }}</span>
          <span class="block md:hidden">{{ t('gallery') }}</span>
        </Button>
      </div>

      <Transition mode="out-in" name="gpx-fade">
        <div v-if="isLoading" key="guestbook-loading" data-test="guestbook-loading">
          <masonry :cols="{ default: 4, 1024: 3, 768: 1 }" :gutter="10">
            <div v-for="n in 5" :key="`${n}-loading`" class="mb-3">
              <GuestbookMessageCard is-loading />
            </div>
          </masonry>
        </div>

        <div v-else-if="messages?.length" key="guestbook-messages" class="flex flex-col gap-4 md:gap-8" data-test="guestbook-messages">
          <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-6">
            <p data-test="guestbook-messages-intro" class="font-montserrat font-normal leading-relaxed text-gray-700 md:min-w-0 md:flex-1 md:max-w-2xl">
              {{ t(`guestbook_messages_page.${eventVault?.eventVaultSettings?.shareGuestbookWithGuests ? "message" : "add_only_message"}`) }}
            </p>

            <div v-if="!isGuestbookHidden" class="flex w-full shrink-0 flex-col gap-2 sm:flex-row sm:gap-3 md:w-auto md:justify-end">
              <Button
                data-test="guestbook-write-message-button"
                class="w-full sm:flex-1 md:flex-initial md:w-auto"
                prefix-icon="Pen"
                :disabled="isFeatureDisabled"
                @click="showMessageDrawer('text')"
              >
                {{ t("guestbook_messages_page.write_message_button") }}
              </Button>
              <Button
                v-if="canUploadVideos"
                data-test="guestbook-record-video-button"
                class="w-full sm:flex-1 md:flex-initial md:w-auto"
                prefix-icon="Video"
                :disabled="isFeatureDisabled"
                @click="showMessageDrawer('video')"
              >
                {{ t("guestbook_messages_page.record_video_button") }}
              </Button>
            </div>
          </div>

          <masonry :cols="{ default: 4, 1024: 3, 768: 1 }" :gutter="10">
            <template v-for="(message, index) in messages" :key="message.id ?? `guestbook-msg-${index}`">
              <div v-if="message.type !== GuestbookMessageType.Unknown" class="mb-3">
                <GuestbookMessageCard
                  :message="message"
                  :can-delete="(eventVaultStore.isHost || message.isCreator) && !isFeatureDisabled"
                  :can-edit="message.type === GuestbookMessageType.Text && message.isCreator && !isFeatureDisabled"
                  @edit="openDialog(message, toggleEdit)"
                  @delete="openDialog(message, toggleDelete)"
                  @flag="openDialog(message, toggleFlagContentModal)"
                />
              </div>
            </template>
          </masonry>
        </div>

        <GuestEmptyStatePanel
          v-else
          key="guestbook-empty"
          icon="BookOpen"
          :include-actions-row="!isGuestbookHidden"
          data-test="guestbook-empty-state"
        >
          <template #title>
            {{ eventVault?.eventVaultSettings?.shareGuestbookWithGuests ? t("guestbook_messages_page.empty_state_title") : t("guestbook_messages_page.empty_state_title_not_shared") }}
          </template>
          <template #description>
            {{ eventVault?.eventVaultSettings?.shareGuestbookWithGuests ? t("guestbook_messages_page.empty_state_description") : t("guestbook_messages_page.empty_state_description_not_shared") }}
          </template>
          <template #actions>
            <Button
              data-test="guestbook-empty-write-message-button"
              class="w-full sm:w-auto"
              prefix-icon="Pen"
              :disabled="isFeatureDisabled"
              @click="showMessageDrawer('text')"
            >
              {{ t("guestbook_messages_page.write_message_button") }}
            </Button>
            <Button
              v-if="canUploadVideos"
              data-test="guestbook-empty-record-video-button"
              class="w-full sm:w-auto"
              prefix-icon="Video"
              :disabled="isFeatureDisabled"
              @click="showMessageDrawer('video')"
            >
              {{ t("guestbook_messages_page.record_video_button") }}
            </Button>
          </template>
        </GuestEmptyStatePanel>
      </Transition>
    </div>
  </div>

  <MessageEditModal
    :message="selectedMessage"
    :event-vault-id="eventVaultId"
  />

  <MessageDeleteModal
    :message="selectedMessage"
    :event-vault-id="eventVaultId"
    @success="toast.success({ description: t('guestbook_messages_page.delete_success') })"
    @error="toast.error()"
  />

  <FlagContentModal
    v-if="selectedMessage"
    :event-vault-id="eventVaultId"
    :content="{ type: 'guestbookMessage', content: selectedMessage }"
  />

  <GuestbookMessageDrawer
    :mode="messageDrawerMode"
    :event-vault="eventVault"
    :event-vault-id="eventVaultId"
    :album-id="albumId"
    :is-event-vault-loading="isLoadingEventVault"
    :is-feature-disabled="isFeatureDisabled"
  />
</template>
